import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Smartphone, 
  Globe, 
  Bitcoin, 
  Briefcase,
  ArrowRight,
  Code,
  ShoppingCart,
  Monitor,
  FileCode,
  Wallet,
  Cloud,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Services = () => {
  const services = [
    {
      id: "application-development",
      icon: Smartphone,
      title: "Application Development",
      description: "Custom mobile and enterprise applications tailored to your business needs.",
      features: ["Custom Mobile Apps", "Enterprise Applications", "Cross-Platform Solutions"],
      gradient: "from-blue-600 to-blue-400"
    },
    {
      id: "web-development",
      icon: Globe,
      title: "Web Development",
      description: "Full-stack web solutions from responsive websites to complex platforms.",
      features: ["Full Stack Development", "E-commerce Platforms", "Responsive Websites"],
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      id: "cryptocurrency-development",
      icon: Bitcoin,
      title: "Cryptocurrency Development",
      description: "Blockchain solutions including tokens, smart contracts, and DeFi applications.",
      features: ["Custom Token Creation", "Smart Contracts", "DeFi Solutions", "Secure Wallets"],
      gradient: "from-purple-600 to-blue-500"
    },
    {
      id: "it-consultancy",
      icon: Briefcase,
      title: "IT Consultancy",
      description: "Strategic technology consulting for digital transformation and growth.",
      features: ["Business Tech Strategy", "Digital Transformation", "Cloud Consulting"],
      gradient: "from-blue-500 to-cyan-400"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0F172A]/50" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1764258560300-2346b28b4e7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwY3liZXIlMjB0ZWNoJTIwYmx1ZSUyMGxpZ2h0JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc0OTM4ODM1fDA&ixlib=rb-4.1.0&q=85")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "radial-gradient(ellipse at center, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 0%, transparent 70%)"
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Solutions That Drive Growth
          </h2>
          <p className="text-lg text-slate-300">
            We offer comprehensive IT services to help your business thrive in the digital era.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="grid md:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={cardVariants}
            >
              <Link to={`/services/${service.id}`} data-testid={`service-card-${service.id}`}>
                <div className="group h-full glass rounded-2xl p-8 sm:p-10 glass-hover card-shine cursor-pointer">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 service-icon-glow`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-300 mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Arrow */}
                  <div className="flex items-center gap-2 text-blue-400 font-medium">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Link to="/services">
            <Button
              className="bg-blue-600 text-white rounded-full px-8 py-6 font-semibold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all group"
              data-testid="services-view-all-btn"
            >
              View All Services
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
